<template>
  <div class="add-patient" @keyup.enter="onEnter">
    <mcw-dialog-title>
      Добавить сотрудника
      <mcw-icon-button class="btn_close" @click="close">
        <mcw-material-icon icon="close" />
      </mcw-icon-button>
    </mcw-dialog-title>

    <mcw-dialog-content class="dialog-content" v-if="!bindContractToTreeQuickly">
      <h2 v-if="form.create_patient === '1' && !localContractId" class="attention">
        Необходимо выбрать договор!
      </h2>

      <mcw-layout-grid>
        <mcw-layout-cell desktop="6">
          <mcw-textfield v-model.trim="fullName" label="ФИО" outline />

          <div class="form-group">
            <mcw-textfield
              v-model="form.lastname"
              label="Фамилия*"
              outline
              required
              :valid="validity.lastname"
            />
            <mcw-textfield
              v-model="form.firstname"
              label="Имя*"
              outline
              required
              :valid="validity.firstname"
            />
          </div>

          <div class="form-group">
            <mcw-textfield
              v-model="form.middlename"
              label="Отчество"
              outline
              :valid="validity.middlename"
            />

            <DateField
              style="margin-top:20px;"
              ref="DateField"
              v-model="form.birthdate"
              @valid="isDateValid = $event"
            />
          </div>

          <div class="form-group form-group_centered">
            <span>Пол*:</span>
            <mcw-radio v-model="form.people_sex" label="М" name="gender" value="1" />
            <mcw-radio v-model="form.people_sex" label="Ж" name="gender" value="2" />
          </div>
        </mcw-layout-cell>

        <mcw-layout-cell desktop="6">
          <div class="form-group">
            <mcw-textfield
              v-model="form.snils"
              label="СНИЛС"
              outline
              v-snils
              :valid="validity.snils"
            />
            <mcw-textfield v-model="form.tab_number" label="Табельный номер" outline />
          </div>

          <div class="form-group">
            <mcw-textfield
              v-model="form.phone"
              label="Телефон"
              outline
              v-phone:1
              :valid="validity.phone"
            />
            <mcw-textfield
              v-model="form.email"
              label="E-mail"
              outline
              :valid="validity.email"
            />
          </div>

          <div class="form-group form-group_centered">
            <span><b>Добавить пациента:</b></span>
            <mcw-radio v-model="form.create_patient" label="Да" name="create" value="1" />
            <mcw-radio v-model="form.create_patient" label="Нет" name="create" value="0" />
          </div>

          <div class="form-group">
            <mcw-select
              class="select_dense"
              :class="form.create_patient !== '1' ? 'mdc-select--disabled' : ''"
              v-model="selectedMOType"
              v-if="MOTypes.length"
              outlined
              label="Тип МО"
            >
              <mcw-list-item
                v-for="type of MOTypes"
                :key="type.id_reference"
                :data-value="type.id_reference"
              >
                {{ type.name_reference }}
              </mcw-list-item>
            </mcw-select>

            <AvailableContracts
              @selectContract="setContract"
              :disabled="form.create_patient !== '1' ? true : false"
            />
          </div>

          <div v-if="askPayType" class="form-group">
            <mcw-select
              class="select_dense"
              label="Тип оплаты"
              outlined
              v-model="selectedPaymentType"
            >
              <mcw-list-item
                v-for="type of paymentTypes"
                :key="type.pay_type"
                :data-value="type.pay_type"
              >
                {{ type.pay_name }}
              </mcw-list-item>
            </mcw-select>
          </div>
        </mcw-layout-cell>
      </mcw-layout-grid>
    </mcw-dialog-content>

    <mcw-dialog-content v-else class="quick-bind">
      <h5>
        Укажите контракт для связки с
        <b>{{ selectedItem.parent.data.text }}</b>
      </h5>

      <SearchSelect
        v-if="contracts.length"
        titleWidth="300px"
        placeholder="Доступные договоры"
        label="label"
        :options="contracts"
        :initial="{}"
        :addButton="false"
        :showCounter="false"
        @selectItem="bindContract"
      />
    </mcw-dialog-content>

    <mcw-dialog-footer>
      <span v-if="!bindContractToTreeQuickly && saveBlockReason" class="save-hint">
        {{ saveBlockReason }}
      </span>

      <mcw-dialog-button
        v-if="!bindContractToTreeQuickly"
        @click="addPatient"
        :disabled="canISave || isSaving"
        :class="{ disabled: canISave || isSaving }"
      >
        Создать
      </mcw-dialog-button>

      <mcw-dialog-button
        v-if="!bindContractToTreeQuickly"
        action="dismiss"
        @click="close"
      >
        отмена
      </mcw-dialog-button>

      <mcw-dialog-button
        v-else
        @click="bindContractToTreeQuickly = false"
      >
        отмена
      </mcw-dialog-button>
    </mcw-dialog-footer>
  </div>
</template>

<script>
import { utils } from '../helpers/utils';
import AvailableContracts from './AvailableContracts.vue';
import SearchSelect from './SearchSelect/SearchSelect.vue';
import DateField from './DateField.vue';

// Управляющие символы (таб, перевод строки и т.п.), NBSP, разделители строк → пробел
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_RE = /[\u0000-\u001F\u007F-\u009F\u00A0\u2028\u2029]/g;
// Невидимые символы → удаляем
const ZERO_WIDTH_RE = /[\u200B-\u200D\u2060\uFEFF]/g;
// В ФИО допустимы только буквы, пробел, дефис, апостроф
const NAME_FORBIDDEN_RE = /[^A-Za-zА-Яа-яЁё\s'-]/g;
// Имя целиком на кириллице или целиком на латинице (ловит «Иванoв» с латинской «o»)
const CYRILLIC_NAME_RE = /^[А-Яа-яЁё]+(?:[ '-][А-Яа-яЁё]+)*$/;
const LATIN_NAME_RE = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS = 11;
const SNILS_DIGITS = 11;

const stripTechChars = (value) => String(value || '')
  .replace(ZERO_WIDTH_RE, '')
  .replace(CONTROL_CHARS_RE, ' ');

// Очистка «на лету»: хвостовой пробел не трогаем, иначе не напечатать второе слово
const sanitizeText = (value) => stripTechChars(value)
  .replace(/ {2,}/g, ' ')
  .replace(/^ +/, '');

const sanitizeName = (value) => sanitizeText(String(value || '').replace(NAME_FORBIDDEN_RE, ''));

const sanitizeNoSpaces = (value) => stripTechChars(value).replace(/\s+/g, '');

// Маскированные поля: только убираем техсимволы, формат держит маска
const sanitizeMasked = (value) => String(value || '')
  .replace(ZERO_WIDTH_RE, '')
  .replace(CONTROL_CHARS_RE, '');

// Финальная нормализация перед отправкой
const finalizeText = (value) => stripTechChars(value).replace(/\s+/g, ' ').trim();

const getDigits = (value) => String(value || '').replace(/\D/g, '');

// Маска СНИЛС: только цифры, не больше 11, формат 000-000-000 00.
// Разделитель ставится только перед следующей цифрой — backspace не «залипает» на дефисе
const formatSnils = (value) => {
  const digits = getDigits(value).slice(0, SNILS_DIGITS);
  const groups = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 9)]
    .filter(Boolean)
    .join('-');

  return digits.length > 9 ? `${groups} ${digits.slice(9)}` : groups;
};

const FIELD_SANITIZERS = {
  lastname: sanitizeName,
  firstname: sanitizeName,
  middlename: sanitizeName,
  tab_number: sanitizeText,
  email: sanitizeNoSpaces,
  snils: formatSnils,
  phone: sanitizeMasked,
};

const isNameValid = (value) => {
  const name = finalizeText(value);
  return CYRILLIC_NAME_RE.test(name) || LATIN_NAME_RE.test(name);
};

// Маска v-phone:1 подставляет «8» после первой цифры — одну цифру считаем пустым полем
const isPhoneEmpty = (value) => getDigits(value).length <= 1;

const FIELD_LABELS = {
  lastname: 'фамилия',
  firstname: 'имя',
  middlename: 'отчество',
  snils: `СНИЛС (нужно ${SNILS_DIGITS} цифр)`,
  phone: `телефон (нужно ${PHONE_DIGITS} цифр)`,
  email: 'e-mail',
};

export default {
  name: 'AddPatient',

  components: {
    AvailableContracts,
    SearchSelect,
    DateField,
  },

  mixins: [utils],

  props: {
    selectedItem: Object,
    MOTypes: Array,
  },

  emits: ['close'],

  data() {
    return {
      localContractId: null,
      selectedMOType: null,
      askPayType: false,
      paymentTypes: [],
      selectedPaymentType: null,
      isDateValid: true,
      isSaving: false,
      bindContractToTreeQuickly: false,

      form: {
        lastname: '',
        firstname: '',
        middlename: '',
        birthdate: '',
        people_sex: '',
        create_patient: '1',
        snils: '',
        tab_number: '',
        phone: '',
        email: '',
      },
      fullName: '',
    };
  },

  computed: {
    currentContract() {
      return this.$store.state.contractsData.currentContract;
    },

    contracts() {
      return this.$store.getters['contractsData/getContracts']
        .filter((i) => i.for_medsafe === 1 && i.id_contract > 0)
        .map((i) => ({
          ...i,
          label: `${i.id_contract} | ${i.name_contract}`,
        }));
    },

    // Для подсветки: пустое поле не красим, обязательность проверяет canISave
    validity() {
      const {
        lastname,
        firstname,
        middlename,
        snils,
        phone,
        email,
      } = this.form;

      return {
        lastname: !lastname || isNameValid(lastname),
        firstname: !firstname || isNameValid(firstname),
        middlename: !middlename || isNameValid(middlename),
        // validateSnils из миксина показывает alert — на лету его не зовём, только при сохранении
        snils: !snils || getDigits(snils).length === SNILS_DIGITS,
        phone: isPhoneEmpty(phone) || getDigits(phone).length === PHONE_DIGITS,
        email: !email || EMAIL_RE.test(email),
      };
    },

    // Причина, по которой нельзя сохранить; пустая строка — можно
    saveBlockReason() {
      const {
        firstname,
        lastname,
        people_sex,
        create_patient,
      } = this.form;

      if (!finalizeText(lastname) || !finalizeText(firstname)) return 'Заполните фамилию и имя';
      if (!people_sex) return 'Укажите пол';

      const invalidFields = Object.keys(this.validity)
        .filter((key) => !this.validity[key])
        .map((key) => FIELD_LABELS[key]);

      if (invalidFields.length) return `Проверьте: ${invalidFields.join(', ')}`;
      if (!this.isDateValid) return 'Проверьте дату рождения';
      if (create_patient === '1' && !this.localContractId) return 'Выберите договор';
      if (this.askPayType && !this.selectedPaymentType) return 'Выберите тип оплаты';

      return '';
    },

    canISave() {
      return !!this.saveBlockReason;
    },
  },

  watch: {
    form: {
      deep: true,
      handler(form) {
        Object.keys(FIELD_SANITIZERS).forEach((key) => {
          const cleaned = FIELD_SANITIZERS[key](form[key]);
          if (cleaned !== form[key]) this.form[key] = cleaned;
        });
      },
    },

    fullName(val) {
      const cleaned = sanitizeName(val);
      if (cleaned !== val) {
        this.fullName = cleaned;
        return;
      }

      if (!val.trim()) {
        this.form.lastname = '';
        this.form.firstname = '';
        this.form.middlename = '';
        return;
      }

      const parts = val.trim().split(/\s+/);

      this.form.lastname = parts[0] || '';
      this.form.firstname = parts[1] || '';
      this.form.middlename = parts.slice(2).join(' ');
    },
  },

  async created() {
    if (this.currentContract) {
      this.localContractId = this.currentContract;
    }

    await this.loadPaymentTypes();
  },

  methods: {
    setContract(contract) {
      if (contract.id_contract < 0) {
        this.bindContractToTreeQuickly = true;
        return;
      }

      this.localContractId = contract.id_contract;
      this.askPayType = !!contract.ask_pay_type;
    },

    async bindContract(contract) {
      try {
        const params = {
          tree: [{
            id_tree: this.selectedItem.data.parentId,
            exception: 0,
          }],
          id_contract: contract.id_contract,
          delete: 0,
        };

        this.emitter.emit('globalLoading', true);
        await this.$store.dispatch('treeData/treeToContract', params);
      } catch (err) {
        console.error(err);
      } finally {
        this.emitter.emit('globalLoading', false);
        this.bindContractToTreeQuickly = false;
      }
    },

    close() {
      this.bindContractToTreeQuickly = false;
      this.$emit('close');
    },

    onEnter() {
      if (this.bindContractToTreeQuickly) return;
      this.addPatient();
    },

    async addPatient() {
      if (this.canISave || this.isSaving) return;

      if (this.form.snils && !this.validateSnils(this.form.snils)) {
        this.emitter.emit('message', 'Некорректный СНИЛС');
        return;
      }

      const dateField = this.$refs.DateField;

      if (!dateField.validate(this.form.birthdate)) {
        this.emitter.emit('message', 'Дата рождения некорректна или отсутствует');
        return;
      }

      const params = Object.fromEntries(
        Object.entries(this.form).map(([key, value]) => [
          key,
          typeof value === 'string' ? finalizeText(value) : value,
        ]),
      );

      if (isPhoneEmpty(params.phone)) params.phone = '';

      params.id_contract = this.form.create_patient === '1' && this.localContractId
        ? this.localContractId
        : 0;

      params.type_mo = this.selectedMOType || 0;
      params.id_tree = this.selectedItem.data.id;

      if (this.askPayType) {
        params.pay_type = this.selectedPaymentType;
      }

      this.isSaving = true;

      // Запрос на сервере бывает долгим (до пары минут), поэтому UI его не ждёт,
      // как и до правок. Результат ловим в фоне — окно к этому моменту уже закрыто,
      // поэтому в сообщении указываем, кого не удалось добавить
      const failMessage = `Не удалось добавить пациента ${params.lastname} ${params.firstname}`;

      this.$store.dispatch('treeData/addPatient', params)
        .then((result) => {
          if (result === false) this.emitter.emit('message', failMessage);
        })
        .catch((err) => {
          console.error(err);
          this.emitter.emit('message', failMessage);
        });

      this.emitter.emit('globalLoading', true);

      try {
        this.$store.commit('treeData/SET_AMOUNT', params.id_tree);

        await this.$store.dispatch(
          'employeesData/getLastPatients',
          { includeServices: false },
          { root: true },
        );

        this.close();
      } catch (err) {
        console.error(err);
      } finally {
        this.emitter.emit('globalLoading', false);
        this.isSaving = false;
      }
    },

    async loadPaymentTypes() {
      const result = await this.$store.dispatch('employeesData/loadPaymentTypes');
      if (result) {
        this.paymentTypes = result;
      }
    },
  },
};
</script>

<style>
.dialog-content {
  overflow: visible !important;
}

.quick-bind {
  min-width: 400px;
  min-height: 300px;
}

.form-group {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.form-group_centered {
  align-items: center;
}

.disabled {
  opacity: 0.6;
}

.save-hint {
  margin-right: auto;
  padding-left: 8px;
  font-size: 13px;
  color: #b00020;
}
</style>
